import { altLocale } from '@utils/locale';
import { MARQUES, BRANDS } from './plp/constants';

/** Edge-safe path joiner (no Node.js `path` module). */
function joinPath(...segments: string[]): string {
  if (segments.length === 1) return segments[0].replace(/\/+/g, '/');
  const hasLeading = segments[0] === '/';
  const rest = hasLeading ? segments.slice(1) : segments;
  const s = rest.filter(Boolean).join('/').replace(/\/+/g, '/');
  return hasLeading ? '/' + s : s;
}

export function localePath(local: string, ...path: string[]): string {
  return joinPath('/', local, ...path);
}

export function localeAltPath(local: string, ...path: string[]): string {
  return joinPath('/', altLocale(local), ...path);
}

export function localePathAbs(local: string, ...path: string[]): string {
  const host = process.env.BASE_URL || '';
  return host + joinPath(localePath(local, ...path));
}

export function localeAltPathAbs(local: string, ...path: string[]): string {
  const host = process.env.BASE_URL || '';
  return host + joinPath(localeAltPath(local, ...path));
}

export const fullyEncode = (path: string) => {
  const decode = path
    .replace(/%21/g, '!')
    .replace(/%27/g, "'")
    .replace(/%28/g, '(')
    .replace(/%29/g, ')')
    .replace(/%2A/g, '*')
    .replace(/%(?![0-9][0-9a-fA-F]+)/g, '%25');

  let encodedURI;
  try {
    encodedURI = encodeURI(decodeURI(decode)).replace(
      /[!'()*]/g,
      (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
    );
  } catch (error) {
    console.warn(`URI ${decode} is malformed: ${error}`);
    encodedURI = null;
  }

  return encodedURI;
};

export const fullyEncodeComponent = (path: string) => {
  const decode = path
    .replace(/%21/g, '!')
    .replace(/%27/g, "'")
    .replace(/%28/g, '(')
    .replace(/%29/g, ')')
    .replace(/%2A/g, '*')
    .replace(/%(?![0-9][0-9a-fA-F]+)/g, '%25');

  let encodedURIComponent;
  try {
    encodedURIComponent = encodeURIComponent(
      decodeURIComponent(decode)
    ).replace(
      /[!'()*]/g,
      (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
    );
  } catch (error) {
    console.warn(`URI ${decode} is malformed: ${error}`);
    encodedURIComponent = null;
  }

  return encodedURIComponent;
};

export const generateBrandURL = (slug: string, locale?: string) =>
  locale === 'fr'
    ? `produits/${MARQUES}/${slug
        .toLowerCase()
        .replace(/\s/g, '-')
        .replace(/([a-zA-Z0-9])&([a-zA-Z0-9])/g, '$1-$2')}`
    : `products/${BRANDS}/${slug
        .toLowerCase()
        .replace(/\s/g, '-')
        .replace(/([a-zA-Z0-9])&([a-zA-Z0-9])/g, '$1-$2')}`;

export const sortSearchParams = (
  searchParams: URLSearchParams
): URLSearchParams => {
  const sortedParams = new URLSearchParams(
    Array.from(new URLSearchParams(searchParams).entries()).sort(
      ([keyA], [keyB]) => keyA.localeCompare(keyB)
    )
  );

  return sortedParams;
};

export const sanitizeSearchParams = (
  searchParams: URLSearchParams,
  allowList: string[]
): URLSearchParams => {
  const filteredSearchParams = new URLSearchParams();

  for (const param of searchParams.keys()) {
    const isAllowed = allowList.some((allowedKey) => {
      const regexPattern = new RegExp(
        `^${allowedKey.replace(/\./g, '\\.').replace(/\*/g, '.*')}$`
      );
      return allowedKey.includes('*')
        ? regexPattern.test(param)
        : allowedKey === param;
    });

    if (isAllowed) {
      filteredSearchParams.append(param, searchParams.get(param) || '');
    }
  }

  const sortedParams = sortSearchParams(filteredSearchParams);

  return sortedParams;
};

export function mergeQueryStrings(
  queryString = '',
  extraQueryString = ''
): string {
  const params = new URLSearchParams(queryString.replace(/^\?/, ''));
  const extraParams = new URLSearchParams(extraQueryString.replace(/^\?/, ''));

  const mergedParams = new URLSearchParams();

  params.forEach((value: string, key: string): void => {
    mergedParams.append(key, value);
  });

  extraParams.forEach((value: string, key: string): void => {
    if (!mergedParams.has(key)) {
      mergedParams.append(key, value);
    }
  });

  const sortedParams = new URLSearchParams([...mergedParams.entries()].sort());
  const paramString = sortedParams.toString();

  return paramString ? `?${paramString}` : '';
}

export const slugify = (slug: string) => {
  // remove slashes (looking at you, DU/ER)
  const withoutSlashes = slug.replace(/\//g, '');

  // remove apostrophes
  const withoutApostrophes = withoutSlashes.replace(/'/g, '');

  // remove diacritics and accents
  const normalizedString = withoutApostrophes
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  // replace spaces and non-URL-safe characters with hyphens
  const urlFriendlyString = normalizedString
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

  // ronvert the string to lowercase
  const lowercaseURL = urlFriendlyString.toLowerCase();

  return lowercaseURL;
};

export const stripTrailingSlash = (path: string) =>
  path.length > 1 ? path.replace(/\/$/, '') : path;

export const addLocaleToUrl = (url: string, locale?: string) => {
  if (url.includes('/en/') || url.includes('/fr/') || !locale) {
    return url;
  }
  return localePath(locale, url);
};
